import { environment } from './../../clientapp/src/environments/environment';
import { inject, Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Capacitor } from '@capacitor/core';
import { App, AppInfo } from '@capacitor/app';
import { StorageService } from '../../clientapp/src/services/storage/storage.service';
import { ConsentStatus, ConsentType, FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Posthog } from '@capawesome/capacitor-posthog';
import { ConsentSettings } from '../../clientapp/src/services/consent.service';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import * as StackTrace from 'stacktrace-js';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  storageService = inject(StorageService);

  private appInsights?: ApplicationInsights;
  private sessionId?: string;
  private consentSettings?: ConsentSettings;

  private isMobile = Capacitor.isNativePlatform();

  constructor() {
    if (environment.environmentName != 'development') {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.appInsightsKey,
          enableAutoRouteTracking: true,
          disableAjaxTracking: true,
          disableFetchTracking: true,
        },
      });
    }
  }

  updateConsentSettings(settings: ConsentSettings | undefined) {
    this.consentSettings = settings;

    this.updateTrackingServices();
  }

  private async updateTrackingServices() {
    if (!this.consentSettings) return;

    await this.firebseSetConsent(
      ConsentType.AnalyticsStorage,
      this.consentSettings.analytics ? ConsentStatus.Granted : ConsentStatus.Denied
    );
    await this.firebseSetConsent(
      ConsentType.FunctionalityStorage,
      this.consentSettings.analytics ? ConsentStatus.Granted : ConsentStatus.Denied
    );
    await this.firebseSetConsent(ConsentType.AdPersonalization, ConsentStatus.Denied);
    await this.firebseSetConsent(ConsentType.AdStorage, ConsentStatus.Denied);
    await this.firebseSetConsent(ConsentType.AdUserData, ConsentStatus.Denied);
    await this.firebseSetConsent(ConsentType.PersonalizationStorage, ConsentStatus.Denied);
  }

  async setUserContext(userId?: string | null, email?: string) {
    if (userId) {
      this.appInsights?.setAuthenticatedUserContext(userId, email);

      if (!this.consentSettings?.analytics) return;

      await FirebaseAnalytics.setUserId({
        userId: userId,
      });

      await Posthog.alias({
        alias: userId,
      });

      await Posthog.identify({
        distinctId: userId,
      });

      await this.posthogRegister('userId', userId);

      if (this.isMobile) {
        await FirebaseCrashlytics.setUserId({
          userId: userId,
        });
      }
    } else {
      this.appInsights?.clearAuthenticatedUserContext();

      await FirebaseAnalytics.setUserId({
        userId: null,
      });
    }
  }

  logException(exception: any, context?: string, properties: any = {}) {
    const errorObj =
      exception instanceof Error
        ? exception
        : new Error(typeof exception === 'string' ? exception : JSON.stringify(exception));

    console.error(errorObj);

    this.appInsights?.trackException({
      exception: errorObj,
      severityLevel: 3,
      properties: { context, ...properties },
    });

    if (this.isMobile) {
      StackTrace.fromError(errorObj).then(async (stacktrace) => {
        await FirebaseCrashlytics.recordException({
          message: context || 'Error',
          stacktrace,
        });
      });
    }
  }

  async logEvent(name: string, properties: any = {}) {
    this.appInsights?.trackEvent({ name }, properties);

    if (!this.consentSettings?.analytics) return;

    await FirebaseAnalytics.logEvent({
      name,
      params: properties,
    });

    await Posthog.capture({
      event: name,
      properties: properties,
    });
  }

  async logPageView(name: string) {
    this.appInsights?.trackPageView({ name });

    if (!this.consentSettings?.analytics) return;

    await FirebaseAnalytics.setCurrentScreen({
      screenName: name,
    });
  }

  async getAppInfo(): Promise<AppInfo | undefined> {
    if (!Capacitor.isNativePlatform()) {
      return { name: 'web-app', id: 'web-app-id', version: 'web-version', build: 'web-build' };
    }

    let appInfo: AppInfo | undefined;

    try {
      appInfo = await App.getInfo();
    } catch (err) {
      console.error('Failed to get app info', err);
    }

    return appInfo;
  }

  async init() {
    this.appInsights?.loadAppInsights();
    await this.initPosthog();

    await this.ensureCrashlyticsEnabled();
    await this.ensureFirebaseAnalyticsEnabled();

    await this.initSessionId();
    await this.initVersionInfo();

    window.addEventListener('unhandledrejection', (event) => {
      this.logException(event.reason, 'Unhandled Promise Rejection');
    });

    // Global window error
    window.onerror = (message, source, lineno, colno, error) => {
      this.logException(error || new Error(String(message)), 'Window Error');
    };

    this.logEvent('LoggerServiceInitialized', {
      sessionId: this.sessionId,
    });
  }

  private async initVersionInfo() {
    let appInfo = await this.getAppInfo();

    try {
      this.appInsights?.addTelemetryInitializer((envelope) => {
        envelope.tags = envelope.tags || {};
        envelope.data = envelope.data || {};

        envelope.tags['ai.session.id'] = this.sessionId || '';
        envelope.data['session_id'] = this.sessionId || '';
        envelope.data['platform'] = Capacitor.getPlatform();
        envelope.data['appVersion'] = appInfo?.version || '';
        envelope.data['appBuild'] = appInfo?.build || '';
        envelope.data['source'] = 'frontend';
        envelope.data['environment'] = environment.environmentName;
      });

      await this.setFirebaseAndPosthogUserProperties([
        { key: 'sessionId', value: this.sessionId || '' },
        { key: 'appVersion', value: appInfo?.version || '' },
        { key: 'appBuild', value: appInfo?.build || '' },
        { key: 'platform', value: Capacitor.getPlatform() },
        { key: 'source', value: 'frontend' },
        { key: 'environment', value: environment.environmentName },
      ]);
    } catch (err) {
      this.logException(err, 'Failed to initialize version info');
    }
  }

  private async initSessionId() {
    try {
      let id = await this.storageService.get<string>('sp_session_id');

      if (!id) {
        id =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).substring(2, 11);
        await this.storageService.save('sp_session_id', id);
      }
      this.sessionId = id;
    } catch (err) {
      console.error('Failed to initialize session id', err);
    }
  }

  private async ensureFirebaseAnalyticsEnabled() {
    await FirebaseAnalytics.setEnabled({
      enabled: true,
    });
  }

  private async initPosthog() {
    await Posthog.setup({
      apiKey: 'phc_aHPN64eyu1UCYt4tuE3YyiyNd2OHpvM8FMZobLIzrwf',
      host: 'https://eu.i.posthog.com',
    });
  }

  private async setFirebaseAndPosthogUserProperties(properties: { key: string; value: string }[]) {
    if (!this.consentSettings?.analytics) return;

    for (let index = 0; index < properties.length; index++) {
      const property = properties[index];

      await FirebaseAnalytics.setUserProperty({
        key: property.key,
        value: property.value,
      });

      await this.posthogRegister(property.key, property.value);

      if (this.isMobile) {
        await FirebaseCrashlytics.setCustomKey({
          key: property.key,
          value: property.value,
          type: 'string',
        });
      }
    }
  }

  private async ensureCrashlyticsEnabled() {
    try {
      if (!this.isMobile) return;

      await FirebaseCrashlytics.setEnabled({ enabled: true });
    } catch (err) {
      this.logException(err, 'Failed to initialize Crashlytics');
    }
  }

  private async posthogRegister(key: string, value: string) {
    try {
      await Posthog.register({
        key: key,
        value: value,
      });
    } catch (err) {
      console.log('POSTHOG Error registering property', key, value);
    }
  }

  private async firebseSetConsent(type: ConsentType, status: ConsentStatus) {
    try {
      await FirebaseAnalytics.setConsent({
        type: type,
        status: status,
      });
    } catch (err) {
      console.log('FirebaseAnalytics set consent error', type);
    }
  }
}
